import React, { Component } from 'react';
import Modal from '../../components/Modal';
import ProjectIssueCard from './ProjectIssueCard';
import { urlMap, getAuthHeaders } from '../../../utils';

const SCOPE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'structural', label: 'Structural' },
  { key: 'thematic', label: 'Thematic' },
];

/**
 * StructuralThematicModal
 *
 * Displays structural and thematic change proposals from the agent.
 * Users can approve or dismiss each proposal. All must be resolved
 * before PlanRewriteStep can execute.
 *
 * Props:
 *  - isOpen: boolean
 *  - onClose: () => void
 *  - project: object
 */
export default class StructuralThematicModal extends Component {
  state = {
    issues: [],
    loading: true,
    error: null,
    actionBusy: {},
    actionErrors: {},
    activeScope: 'all',
  };

  componentDidMount() {
    // Fetch issues on initial mount if project is available
    if (this.props.project?.id) {
      this.fetchIssues();
    }
  }

  componentDidUpdate(prevProps) {
    // For standalone modal: fetch when opened
    if (this.props.isOpen && !prevProps.isOpen) {
      this.fetchIssues();
    }
    // For embedded tab: fetch when project changes
    if (this.props.project?.id !== prevProps.project?.id) {
      this.fetchIssues();
    }
  }

  fetchIssues = async () => {
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(
        urlMap.getStructuralThematicIssues(this.props.project.id),
        { headers: { 'Content-Type': 'application/json', ...getAuthHeaders() }, credentials: 'include' },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const issues = await res.json();
      this.setState({ issues: Array.isArray(issues) ? issues : [], loading: false });
    } catch (err) {
      console.error('[StructuralThematicModal] Fetch error:', err);
      this.setState({ error: err.message || 'Failed to load changes', loading: false, issues: [] });
    }
  };

  isIssueResolved = (issue) => {
    const status = String(issue?.status || 'pending').toLowerCase();
    return ['approved', 'dismissed', 'applied', 'done', 'resolved'].includes(status);
  };

  handleApprove = async (issueId, options = {}) => {
    const { skipSyncCallbacks = false } = options;
    this.setState((s) => ({ actionBusy: { ...s.actionBusy, [issueId]: true } }));
    try {
      const res = await fetch(
        urlMap.updateStructuralThematicIssueStatus(this.props.project.id, issueId),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ status: 'approved' }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      this.setState((s) => ({
        issues: s.issues.map((i) => (i.id === issueId ? updated : i)),
        actionBusy: { ...s.actionBusy, [issueId]: false },
      }));
      if (!skipSyncCallbacks) {
        this.props.onRefreshProject?.();
        this.props.onIssuesUpdated?.();
        this.props.onIssuesCountChanged?.();
      }
    } catch (err) {
      this.setState((s) => ({
        actionBusy: { ...s.actionBusy, [issueId]: false },
        actionErrors: { ...s.actionErrors, [issueId]: 'Failed to approve' },
      }));
    }
  };

  handleDismiss = async (issueId, options = {}) => {
    const { skipSyncCallbacks = false } = options;
    this.setState((s) => ({ actionBusy: { ...s.actionBusy, [issueId]: true } }));
    try {
      const res = await fetch(
        urlMap.updateStructuralThematicIssueStatus(this.props.project.id, issueId),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          credentials: 'include',
          body: JSON.stringify({ status: 'dismissed' }),
        },
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const updated = await res.json();
      this.setState((s) => ({
        issues: s.issues.map((i) => (i.id === issueId ? updated : i)),
        actionBusy: { ...s.actionBusy, [issueId]: false },
      }));
      if (!skipSyncCallbacks) {
        this.props.onRefreshProject?.();
        this.props.onIssuesUpdated?.();
        this.props.onIssuesCountChanged?.();
      }
    } catch (err) {
      this.setState((s) => ({
        actionBusy: { ...s.actionBusy, [issueId]: false },
        actionErrors: { ...s.actionErrors, [issueId]: 'Failed to dismiss' },
      }));
    }
  };

  handleApproveAll = async () => {
    const unresolved = this.getFilteredIssues().filter((i) => !this.isIssueResolved(i));
    for (const issue of unresolved) {
      await this.handleApprove(issue.id, { skipSyncCallbacks: true });
    }
    await this.fetchIssues();
    this.props.onRefreshProject?.();
    this.props.onIssuesUpdated?.();
    this.props.onIssuesCountChanged?.();
  };

  handleDismissAll = async () => {
    const unresolved = this.getFilteredIssues().filter((i) => !this.isIssueResolved(i));
    for (const issue of unresolved) {
      await this.handleDismiss(issue.id, { skipSyncCallbacks: true });
    }
    await this.fetchIssues();
    this.props.onRefreshProject?.();
    this.props.onIssuesUpdated?.();
    this.props.onIssuesCountChanged?.();
  };

  getFilteredIssues = () => {
    const { issues, activeScope } = this.state;
    if (activeScope === 'all') return issues;
    return issues.filter((i) => i.scope === activeScope);
  };

  renderInnerContent() {
    const { loading, error, actionBusy, actionErrors, activeScope, issues } = this.state;
    const { embedded } = this.props;

    const filtered = this.getFilteredIssues();
    const pendingCount = issues.filter((i) => !this.isIssueResolved(i)).length;
    const allResolved = issues.length > 0 && pendingCount === 0;
    const structuralPending = issues.filter((i) => i.scope === 'structural' && !this.isIssueResolved(i)).length;
    const thematicPending = issues.filter((i) => i.scope === 'thematic' && !this.isIssueResolved(i)).length;

    const statusOrder = { pending: 0, approved: 1, dismissed: 2, applied: 3 };
    const scopeOrder = { structural: 0, thematic: 1 };
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const sorted = [...filtered].sort((a, b) => {
      const bySeverity = (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9);
      if (bySeverity !== 0) return bySeverity;
      const byStatus = (statusOrder[a.status] ?? 9) - (statusOrder[b.status] ?? 9);
      if (byStatus !== 0) return byStatus;
      return (scopeOrder[a.scope] ?? 9) - (scopeOrder[b.scope] ?? 9);
    });

    const filteredPendingCount = filtered.filter((i) => !this.isIssueResolved(i)).length;

    return (
      <div className="stm-modal-content">
        {!embedded && (
          <div className="stm-modal-header">
            <h2 className="stm-modal-title">Structural & Thematic Changes</h2>
          </div>
        )}

        <div className="stm-tabs">
          {SCOPE_TABS.map((tab) => {
            const count = tab.key === 'all'
              ? issues.length
              : issues.filter((i) => i.scope === tab.key).length;
            return (
              <button
                key={tab.key}
                className={`stm-tab${activeScope === tab.key ? ' stm-tab--active' : ''}`}
                onClick={() => this.setState({ activeScope: tab.key })}
              >
                {tab.label}
                <span className="stm-tab-count">{count}</span>
              </button>
            );
          })}
        </div>

        <div className="stm-body">
          {loading && <p className="stm-loading">Loading…</p>}
          {error && <p className="stm-error">{error}</p>}

          {!loading && issues.length === 0 && (
            <p className="stm-empty">No structural or thematic changes proposed.</p>
          )}

          {pendingCount > 0 && (
            <div className="stm-notice stm-notice--warning">
              Approve or dismiss all changes to unlock the Rewrite Plan step.
              {structuralPending > 0 && <span> {structuralPending} structural</span>}
              {structuralPending > 0 && thematicPending > 0 && <span>,</span>}
              {thematicPending > 0 && <span> {thematicPending} thematic</span>}
              <span> pending.</span>
            </div>
          )}

          {allResolved && (
            <div className="stm-notice stm-notice--success">
              All changes resolved. The Rewrite Plan step is now unblocked.
            </div>
          )}

          {filteredPendingCount > 1 && (
            <div className="stm-bulk-actions">
              <button className="btn primary btn--sm" onClick={this.handleApproveAll}>
                Approve All ({filteredPendingCount})
              </button>
              <button className="btn subtle btn--sm" onClick={this.handleDismissAll}>
                Dismiss All ({filteredPendingCount})
              </button>
            </div>
          )}

          <div className="stm-list">
            {sorted.map((issue) => (
              <ProjectIssueCard
                key={issue.id}
                issue={issue}
                onApprove={() => this.handleApprove(issue.id)}
                onDismiss={() => this.handleDismiss(issue.id)}
                isBusy={!!actionBusy[issue.id]}
                error={actionErrors[issue.id]}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isOpen, onClose, embedded } = this.props;

    if (!isOpen) return null;

    if (embedded) return this.renderInnerContent();

    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        {this.renderInnerContent()}
      </Modal>
    );
  }
}
